<script setup>
import { ref, computed, useCssVars } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import WidgetHead from './WidgetHead.vue';
import WidgetBody from './WidgetBody.vue';
import WidgetFooter from './WidgetFooter.vue';
import ChatHeaderExpanded from 'widget/components/ChatHeaderExpanded.vue';
import ConversationChoicePanel from 'widget/components/ConversationChoicePanel.vue';
import TeamAvailability from 'widget/components/TeamAvailability.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Code from 'dashboard/components/Code.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import { useMapGetter } from 'dashboard/composables/store';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import defaultBubbleLogo from 'dashboard/assets/images/bubble-logo.svg';
import {
  findNextAvailableSlotDetails,
  isInWorkingHours as checkIsInWorkingHours,
  isOnline as checkIsOnline,
} from 'widget/helpers/availabilityHelpers';

const props = defineProps({
  welcomeHeading: {
    type: String,
    default: '',
  },
  welcomeTagline: {
    type: String,
    default: '',
  },
  websiteName: {
    type: String,
    required: true,
  },
  logo: {
    type: String,
    default: '',
  },
  replyTime: {
    type: String,
    default: '',
  },
  workingHours: {
    type: Array,
    default: () => [],
  },
  workingHoursEnabled: {
    type: Boolean,
    default: false,
  },
  timezone: {
    type: String,
    default: 'UTC',
  },
  utcOffset: {
    type: String,
    default: '',
  },
  color: {
    type: String,
    default: '',
  },
  widgetBubblePosition: {
    type: String,
    default: '',
  },
  widgetBubbleLauncherTitle: {
    type: String,
    default: '',
  },
  widgetBubbleType: {
    type: String,
    default: '',
  },
  widgetStyle: {
    type: String,
    default: 'standard',
  },
  selectedFeatureFlags: {
    type: Array,
    default: () => [],
  },
  webWidgetScript: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const { replaceInstallationName } = useBranding();
const globalConfig = useMapGetter('globalConfig/get');

const isChatMode = ref(false);
const [isWidgetVisible, toggleWidget] = useToggle(true);

const activeTabIndex = ref(0);

const tabs = computed(() => [
  {
    label: t(
      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.PREVIEW'
    ),
  },
  {
    label: t(
      'INBOX_MGMT.WIDGET_BUILDER.WIDGET_OPTIONS.WIDGET_VIEW_OPTION.SCRIPT'
    ),
  },
]);

const isPreviewTab = computed(() => activeTabIndex.value === 0);

const widgetScript = computed(() => {
  if (!props.webWidgetScript) return '';

  const options = {
    position: props.widgetBubblePosition,
    type: props.widgetBubbleType,
    launcherTitle: props.widgetBubbleLauncherTitle,
  };

  const script = props.webWidgetScript;
  return (
    script.substring(0, 13) +
    t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_SETTINGS', {
      options: JSON.stringify(options),
    }) +
    script.substring(13)
  );
});

const quotedScriptValueFor = key =>
  widgetScript.value.match(new RegExp(`${key}: ['"]([^'"]+)['"]`))?.[1] || '';

const baseUrlFromVariable = () =>
  widgetScript.value.match(
    /(?:var|let|const)\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/
  )?.[1] || '';

const widgetScriptDetails = computed(() => [
  {
    key: 'baseUrl',
    label: t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_DETAILS.BASE_URL'),
    value: quotedScriptValueFor('baseUrl') || baseUrlFromVariable(),
  },
  {
    key: 'websiteToken',
    label: t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_DETAILS.WEBSITE_TOKEN'),
    value: quotedScriptValueFor('websiteToken'),
  },
]);

const codepenTitle = computed(() =>
  replaceInstallationName(
    t('INBOX_MGMT.WIDGET_BUILDER.SCRIPT_DETAILS.CODEPEN_TITLE', {
      websiteName: props.websiteName,
    })
  )
);

const replyTimeText = computed(() => {
  switch (props.replyTime) {
    case 'in_a_few_minutes':
      return t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_MINUTES');
    case 'in_a_day':
      return t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_DAY');
    default:
      return t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_HOURS');
  }
});

const workingHoursConfig = computed(() =>
  props.workingHours.map(slot => useCamelCase(slot))
);

const availabilityOffset = computed(() => props.utcOffset || props.timezone);

const isInWorkingHours = computed(() =>
  checkIsInWorkingHours(
    new Date(),
    availabilityOffset.value,
    workingHoursConfig.value
  )
);

const isOnline = computed(() =>
  checkIsOnline(
    props.workingHoursEnabled,
    new Date(),
    availabilityOffset.value,
    workingHoursConfig.value,
    false
  )
);

const previewDayNames = computed(() => [
  t('INBOX_MGMT.WIDGET_BUILDER.DAY_NAMES.SUNDAY'),
  t('INBOX_MGMT.WIDGET_BUILDER.DAY_NAMES.MONDAY'),
  t('INBOX_MGMT.WIDGET_BUILDER.DAY_NAMES.TUESDAY'),
  t('INBOX_MGMT.WIDGET_BUILDER.DAY_NAMES.WEDNESDAY'),
  t('INBOX_MGMT.WIDGET_BUILDER.DAY_NAMES.THURSDAY'),
  t('INBOX_MGMT.WIDGET_BUILDER.DAY_NAMES.FRIDAY'),
  t('INBOX_MGMT.WIDGET_BUILDER.DAY_NAMES.SATURDAY'),
]);

const availabilityText = computed(() => {
  if (isInWorkingHours.value) return replyTimeText.value;

  if (!props.workingHoursEnabled) {
    return t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.BACK_AS_SOON_AS_POSSIBLE');
  }

  const nextSlot = findNextAvailableSlotDetails(
    new Date(),
    availabilityOffset.value,
    workingHoursConfig.value
  );
  if (!nextSlot) {
    return t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.BACK_IN_SOME_TIME');
  }

  if (nextSlot.daysUntilOpen === 1) {
    return t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.BACK_TOMORROW');
  }

  return t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.BACK_ON_WEEKDAY', {
    day: previewDayNames.value[nextSlot.config.dayOfWeek],
  });
});

const availabilityStatusText = computed(() =>
  isOnline.value
    ? t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.ONLINE')
    : t('INBOX_MGMT.WIDGET_BUILDER.BODY.TEAM_AVAILABILITY.OFFLINE')
);

const choicePanelText = computed(() => {
  const hasSupportRequest = props.selectedFeatureFlags.includes(
    'support_request_form'
  );
  const hasAgentConversation =
    props.widgetStyle !== 'helpdesk' ||
    props.selectedFeatureFlags.includes('agent_conversation');

  let description = t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.HELPDESK_DESCRIPTION');
  if (hasSupportRequest && !hasAgentConversation) {
    description = t(
      'INBOX_MGMT.WIDGET_BUILDER.FOOTER.SUPPORT_ONLY_DESCRIPTION'
    );
  } else if (!hasSupportRequest && hasAgentConversation) {
    description = t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.AGENT_ONLY_DESCRIPTION');
  }

  return {
    title: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.HELPDESK_TITLE'),
    description,
    supportTitle: t('INBOX_MGMT.WIDGET_BUILDER.FOOTER.SUPPORT_REQUEST_TITLE'),
    supportDescription: t(
      'INBOX_MGMT.WIDGET_BUILDER.FOOTER.SUPPORT_REQUEST_DESCRIPTION'
    ),
    conversationTitle: t(
      'INBOX_MGMT.WIDGET_BUILDER.FOOTER.AGENT_CONVERSATION_TITLE'
    ),
    conversationDescription: t(
      'INBOX_MGMT.WIDGET_BUILDER.FOOTER.AGENT_CONVERSATION_DESCRIPTION'
    ),
  };
});

const getWidgetConfig = computed(() => ({
  welcomeHeading: props.welcomeHeading,
  welcomeTagline: props.welcomeTagline,
  websiteName: props.websiteName,
  logo: props.logo,
  isDefaultScreen: !isChatMode.value,
  isOnline: isOnline.value,
  replyTime: availabilityText.value,
  color: props.color,
  isHelpdeskMode: props.widgetStyle === 'helpdesk',
  hasSupportRequestForm: props.selectedFeatureFlags.includes(
    'support_request_form'
  ),
  hasAgentConversation:
    props.widgetStyle !== 'helpdesk' ||
    props.selectedFeatureFlags.includes('agent_conversation'),
  hasAttachments: props.selectedFeatureFlags.includes('attachments'),
  hasEmojiPicker: props.selectedFeatureFlags.includes('emoji_picker'),
}));

const getBubblePositionClass = computed(() =>
  props.widgetBubblePosition === 'left' ? 'justify-start' : 'justify-end'
);

const isBubbleExpanded = computed(
  () => !isWidgetVisible.value && props.widgetBubbleType === 'expanded_bubble'
);

const trimmedLauncherTitle = computed(() =>
  (props.widgetBubbleLauncherTitle || '').trim().slice(0, 20)
);

const hasHoverLauncherTitle = computed(
  () =>
    !isWidgetVisible.value &&
    props.widgetBubbleType === 'standard' &&
    trimmedLauncherTitle.value
);

const bubbleLogoUrl = computed(
  () => globalConfig.value?.logoThumbnail || defaultBubbleLogo
);

const darkenedWidgetColor = computed(() => {
  const match = props.color?.match(/^#?([0-9a-f]{6})$/i);
  if (!match) return props.color;

  const factor = 0.9;
  const hex = match[1];
  const channels = [0, 2, 4].map(index => {
    const value = Math.round(
      parseInt(hex.slice(index, index + 2), 16) * factor
    );
    return value.toString(16).padStart(2, '0');
  });

  return `#${channels.join('')}`;
});

useCssVars(() => ({
  'widget-color': props.color,
  'widget-preview-color': props.color,
  'widget-preview-color-dark': darkenedWidgetColor.value,
}));

const widgetPreviewThemeClassList = [
  '[--slate-1:252_252_253]',
  '[--slate-2:249_249_251]',
  '[--slate-3:240_240_243]',
  '[--slate-4:232_232_236]',
  '[--slate-5:224_225_230]',
  '[--slate-6:217_217_224]',
  '[--slate-7:205_206_214]',
  '[--slate-8:185_187_198]',
  '[--slate-9:139_141_152]',
  '[--slate-10:128_131_141]',
  '[--slate-11:96_100_108]',
  '[--slate-12:28_32_36]',
  '[--background-color:253_253_253]',
  '[--text-blue:8_109_224]',
  '[--border-container:236_236_236]',
  '[--border-strong:235_235_235]',
  '[--border-weak:234_234_234]',
  '[--solid-1:255_255_255]',
  '[--solid-2:255_255_255]',
  '[--solid-3:255_255_255]',
  '[--solid-active:255_255_255]',
  '[--solid-amber:252_232_193]',
  '[--solid-blue:218_236_255]',
  '[--solid-iris:230_231_255]',
  '[--alpha-1:67,67,67,0.06]',
  '[--alpha-2:201,202,207,0.15]',
  '[--alpha-3:255,255,255,0.96]',
  '[--black-alpha-1:0,0,0,0.12]',
  '[--black-alpha-2:0,0,0,0.04]',
  '[--border-blue:39,129,246,0.5]',
  '[--white-alpha:255,255,255,0.8]',
  'dark:[--slate-1:17_17_19]',
  'dark:[--slate-2:24_25_27]',
  'dark:[--slate-3:33_34_37]',
  'dark:[--slate-4:39_42_45]',
  'dark:[--slate-5:46_49_53]',
  'dark:[--slate-6:54_58_63]',
  'dark:[--slate-7:67_72_78]',
  'dark:[--slate-8:90_97_105]',
  'dark:[--slate-9:105_110_119]',
  'dark:[--slate-10:119_123_132]',
  'dark:[--slate-11:176_180_186]',
  'dark:[--slate-12:237_238_240]',
  'dark:[--background-color:18_18_19]',
  'dark:[--border-strong:52_52_52]',
  'dark:[--border-weak:38_38_42]',
  'dark:[--solid-1:23_23_26]',
  'dark:[--solid-2:29_30_36]',
  'dark:[--solid-3:44_45_54]',
  'dark:[--solid-active:53_57_66]',
  'dark:[--solid-amber:42_37_30]',
  'dark:[--solid-blue:16_49_91]',
  'dark:[--solid-iris:38_42_101]',
  'dark:[--text-blue:126_182_255]',
  'dark:[--alpha-1:36,36,36,0.8]',
  'dark:[--alpha-2:139,147,182,0.15]',
  'dark:[--alpha-3:36,38,45,0.9]',
  'dark:[--black-alpha-1:0,0,0,0.3]',
  'dark:[--black-alpha-2:0,0,0,0.2]',
  'dark:[--border-blue:39,129,246,0.5]',
  'dark:[--border-container:236,236,236,0]',
  'dark:[--white-alpha:255,255,255,0.1]',
];

const widgetPreviewThemeClasses = computed(() => [
  widgetPreviewThemeClassList,
  {
    '[&_button]:!rounded-none [&_input]:!rounded-none [&_textarea]:!rounded-none':
      props.widgetStyle === 'flat',
  },
]);

const getWidgetBubbleLauncherTitle = computed(() =>
  isWidgetVisible.value ? ' ' : trimmedLauncherTitle.value
);

const handleTabChange = tab => {
  activeTabIndex.value = tabs.value.findIndex(item => item.label === tab.label);
};

const handleToggleWidget = () => {
  toggleWidget();
  if (isWidgetVisible.value) {
    isChatMode.value = false;
  }
};

const copyScriptDetail = async value => {
  await copyTextToClipboard(value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div class="flex flex-col h-full min-h-0 flex-1">
    <div class="flex items-center justify-between mb-6 flex-shrink-0">
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="handleTabChange"
      />

      <div v-if="isPreviewTab" class="flex items-center gap-2">
        <span class="text-heading-3 text-n-slate-11">
          {{ $t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_SCREEN.CHAT') }}
        </span>
        <Switch v-model="isChatMode" />
      </div>
    </div>

    <div class="flex-1 min-h-0 flex flex-col">
      <div
        v-if="isPreviewTab"
        class="flex-1 flex flex-col items-center justify-end pb-4"
      >
        <div
          v-if="isWidgetVisible"
          class="mb-4 flex h-[30rem] w-full max-w-[25rem] shrink-0 flex-col overflow-hidden rounded-[18px] border border-slate-300/80 bg-n-slate-2 shadow-md ring-1 ring-inset ring-white/80 dark:border-white/20 dark:bg-n-solid-1 dark:ring-white/10"
          :class="widgetPreviewThemeClasses"
        >
          <div
            v-if="getWidgetConfig.isDefaultScreen"
            class="min-h-0 w-full flex-1 overflow-hidden bg-n-slate-2 dark:bg-n-solid-1"
          >
            <div class="relative flex h-full min-h-0 flex-col">
              <ChatHeaderExpanded
                class="!px-4 !pb-3 !pt-4 [&_h2]:!mb-1 [&_h2]:!mt-2 [&_h2]:!text-xl [&_img]:!h-8 [&_p]:!text-sm [&_p]:!leading-5"
                :intro-heading="getWidgetConfig.welcomeHeading"
                :intro-body="getWidgetConfig.welcomeTagline"
                :avatar-url="getWidgetConfig.logo"
              />
              <div class="z-50 mt-auto flex w-full flex-col gap-3 p-3">
                <TeamAvailability
                  class="mt-auto !gap-2 !px-4 !py-3 !text-xs"
                  :show-start-button="false"
                  :preview-status="availabilityStatusText"
                  :preview-reply-time="availabilityText"
                />
                <div
                  class="flex w-full flex-col gap-2 rounded-xl border border-solid border-slate-300/80 bg-n-background p-3 shadow-sm ring-1 ring-inset ring-white/80 dark:border-white/10 dark:bg-n-solid-2 dark:ring-white/10"
                >
                  <ConversationChoicePanel
                    compact
                    :show-support-request="
                      getWidgetConfig.hasSupportRequestForm
                    "
                    :show-agent-conversation="
                      getWidgetConfig.hasAgentConversation
                    "
                    :title="choicePanelText.title"
                    :description="choicePanelText.description"
                    :support-title="choicePanelText.supportTitle"
                    :support-description="choicePanelText.supportDescription"
                    :conversation-title="choicePanelText.conversationTitle"
                    :conversation-description="
                      choicePanelText.conversationDescription
                    "
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-else class="flex min-h-0 flex-1 flex-col">
            <WidgetHead :config="getWidgetConfig" />
            <div class="flex min-h-0 flex-1 flex-col">
              <WidgetBody />
              <WidgetFooter :config="getWidgetConfig" />
            </div>
          </div>
          <div class="flex h-8 shrink-0 items-center justify-center px-2">
            <a
              class="flex cursor-pointer items-center gap-1 text-[10px] leading-3 text-n-slate-11 opacity-90 grayscale hover:opacity-100 hover:grayscale-0"
            >
              <img
                class="max-h-3 max-w-3"
                :src="globalConfig.logoThumbnail"
                alt=""
              />
              <span>
                {{
                  replaceInstallationName(
                    $t('INBOX_MGMT.WIDGET_BUILDER.BRANDING_TEXT')
                  )
                }}
              </span>
            </a>
          </div>
        </div>

        <div
          class="flex w-full max-w-[25rem] shrink-0"
          :class="getBubblePositionClass"
        >
          <button
            class="relative group flex items-center justify-center rounded-full cursor-pointer transition-all duration-200"
            :class="[
              hasHoverLauncherTitle
                ? 'bg-transparent'
                : 'bg-[var(--widget-preview-color)]',
              isBubbleExpanded
                ? 'w-auto font-medium text-base text-white dark:text-white h-12 px-4 overflow-hidden shadow-[inset_0_0_0_2px_rgba(255,255,255,0.18),0_0_0_1px_rgba(255,255,255,0.28),0_12px_30px_rgba(15,23,42,0.26)] hover:shadow-[inset_0_0_0_2px_rgba(255,255,255,0.26),0_0_0_1px_rgba(255,255,255,0.36),0_16px_38px_rgba(15,23,42,0.38)]'
                : hasHoverLauncherTitle
                  ? 'w-16 h-16 hover:w-auto overflow-visible'
                  : 'w-16 h-16 overflow-hidden shadow-[inset_0_0_0_2px_rgba(255,255,255,0.18),0_0_0_1px_rgba(255,255,255,0.28),0_12px_30px_rgba(15,23,42,0.26)] hover:shadow-[inset_0_0_0_2px_rgba(255,255,255,0.26),0_0_0_1px_rgba(255,255,255,0.36),0_16px_38px_rgba(15,23,42,0.38)]',
            ]"
            @click="handleToggleWidget"
          >
            <template v-if="!isWidgetVisible && hasHoverLauncherTitle">
              <span
                class="relative z-10 flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-[var(--widget-preview-color)] shadow-[inset_0_0_0_3px_rgba(255,255,255,0.22),0_0_0_1px_rgba(255,255,255,0.42),0_12px_30px_rgba(15,23,42,0.28)]"
                :class="
                  props.widgetBubblePosition === 'right'
                    ? 'order-last'
                    : 'order-first'
                "
              >
                <img
                  :src="bubbleLogoUrl"
                  alt=""
                  draggable="false"
                  class="w-6 h-6 shrink-0"
                />
              </span>
              <div
                class="relative z-0 flex h-[54px] max-w-0 items-center overflow-hidden whitespace-nowrap rounded-full bg-[var(--widget-preview-color-dark)] text-sm font-semibold text-white opacity-0 shadow-[inset_0_0_0_3px_rgba(255,255,255,0.18),0_0_0_1px_rgba(255,255,255,0.34),0_12px_30px_rgba(15,23,42,0.22)] transition-all duration-200 group-hover:max-w-48 group-hover:opacity-100"
                :class="
                  props.widgetBubblePosition === 'right'
                    ? 'order-first -mr-3 rounded-r-[18px] group-hover:pl-5 group-hover:pr-7'
                    : 'order-last -ml-3 rounded-l-[18px] group-hover:pl-7 group-hover:pr-5'
                "
              >
                {{ getWidgetBubbleLauncherTitle }}
              </div>
            </template>
            <img
              v-else-if="!isWidgetVisible"
              :src="bubbleLogoUrl"
              alt=""
              draggable="false"
              class="w-6 h-6 shrink-0"
            />
            <div
              v-if="isBubbleExpanded"
              class="ltr:pl-2.5 rtl:pr-2.5 whitespace-nowrap"
            >
              {{ getWidgetBubbleLauncherTitle }}
            </div>
            <div v-if="isWidgetVisible" class="relative">
              <div
                class="absolute w-0.5 h-8 rotate-45 -translate-y-1/2 bg-white"
              />
              <div
                class="absolute w-0.5 h-8 -rotate-45 -translate-y-1/2 bg-white"
              />
            </div>
          </button>
        </div>
      </div>

      <div v-else class="min-h-0 flex-1 rounded-lg bg-n-slate-2 p-3 min-w-0">
        <div
          class="h-72 overflow-y-auto overscroll-contain rounded-lg [&_code]:!bg-n-slate-2 [&_code]:whitespace-pre-wrap [&_pre]:break-words [&_pre]:whitespace-pre-wrap [&_.hljs-attr]:!text-n-brand [&_.hljs-keyword]:!text-n-brand [&_.hljs-name]:!text-n-brand [&_.hljs-string]:!text-n-brand [&_.hljs-title]:!text-n-brand"
        >
          <Code
            :script="widgetScript"
            lang="html"
            class="!text-start"
            :codepen-title="codepenTitle"
            enable-code-pen
          />
        </div>

        <div
          v-if="widgetScriptDetails.some(item => item.value)"
          class="mt-3 grid shrink-0 gap-2"
        >
          <div
            v-for="item in widgetScriptDetails"
            :key="item.key"
            class="rounded-lg border border-n-weak bg-n-solid-1 p-3"
          >
            <div class="mb-2 text-xs font-medium text-n-slate-11">
              {{ item.label }}
            </div>
            <div class="flex min-w-0 items-center gap-2">
              <code
                class="min-w-0 flex-1 truncate rounded-md bg-n-alpha-black2 px-3 py-2 text-xs text-n-slate-12"
              >
                {{ item.value }}
              </code>
              <button
                type="button"
                class="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-n-solid-3 px-2.5 text-xs font-medium text-n-slate-12 outline outline-1 outline-n-weak transition-colors hover:bg-n-slate-4"
                @click="copyScriptDetail(item.value)"
              >
                <span class="i-lucide-copy size-3.5" />
                <span>{{ t('COMPONENTS.CODE.BUTTON_TEXT') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
